import axios from 'axios';
import {
	readApiMessageFromBody,
	shouldTreatHttpFailureAsSessionExpired,
	showSessionExpiredAlert,
} from '../utils/apiAlert';
import { notifyError } from '../utils/tostfy';

const isBusinessFailure = (body) => {
	if (!body || typeof body !== 'object') return false;
	if (body.success === false) return true;
	const c = body?.code;
	const codeNum = typeof c === 'string' ? parseInt(c, 10) : c;
	return typeof codeNum === 'number' && !Number.isNaN(codeNum) && codeNum !== 200 && codeNum !== 201;
};

const markApiAlertHandled = (carrier) => {
	if (carrier) carrier.__apiAlertHandled = true;
};

const handleAuthenticatedApiFailure = (carrier, httpStatus, body) => {
	const backendMessage = readApiMessageFromBody(body);

	if (shouldTreatHttpFailureAsSessionExpired(httpStatus, body)) {
		markApiAlertHandled(carrier);
		carrier.__sessionExpired = true;
		showSessionExpiredAlert();
		return;
	}

	if (backendMessage) {
		markApiAlertHandled(carrier);
		notifyError(backendMessage);
	}
};

/** Axios success response or error object returned from handleApiCall */
export const getApiErrorMessage = (response, fallback = '') => {
	if (!response) return fallback;
	if (response.__apiAlertHandled || response.__sessionExpired) return '';
	const data = response?.response?.data ?? response?.data;
	if (typeof data === 'string' && data.trim()) return data.trim();
	const fromBody = readApiMessageFromBody(data);
	if (fromBody) return fromBody;
	const topLevel = response?.message ?? response?.errorMessage;
	if (typeof topLevel === 'string' && topLevel.trim()) return topLevel.trim();
	return fallback;
};

export const isSessionExpiredResponse = (response) => Boolean(response?.__sessionExpired);

/**
 * @param {string} method
 * @param {string} url
 * @param {object} data
 * @param {{ bearerOverride?: string, skipAuthorization?: boolean }} [options]
 */
export const handleApiCall = async (method = '', url = '', data = {}, options = {}) => {
	const { bearerOverride, skipAuthorization } = options;
	const normalizedMethod = String(method || 'get').toLowerCase();

	let token;
	if (bearerOverride != null && bearerOverride !== '') {
		token = bearerOverride;
	} else if (skipAuthorization) {
		token = null;
	} else {
		token = localStorage?.getItem('sessionToken') || null;
	}

	const useSessionAuth = !skipAuthorization && !bearerOverride && token;
	const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
	const headers = {
		...(token ? { Authorization: `Bearer ${token}` } : {}),
	};
	if (!isFormData) {
		headers['Content-Type'] = 'application/json';
	}

	const axiosConfig = {
		method: normalizedMethod,
		url: `${process.env.REACT_APP_API}${url}`,
		headers,
	};
	if (normalizedMethod !== 'get') axiosConfig.data = data;

	try {
		const response = await axios(axiosConfig);
		if (useSessionAuth && isBusinessFailure(response?.data)) {
			handleAuthenticatedApiFailure(response, response?.status, response?.data);
		}
		return response;
	} catch (error) {
		const res = error.response;
		if (useSessionAuth) {
			handleAuthenticatedApiFailure(error, res?.status, res?.data);
		}
		return error;
	}
};
